import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Send, ArrowLeft, Eye, Image as ImageIcon, Type, AlignLeft, Square, TrendingUp, FileText, List, ChevronUp, ChevronDown, GripVertical, ChevronRight, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from './RichTextEditor';

const BRAND_COLORS = [
  { name: 'White', value: '#ffffff' },
  { name: 'Light Gray', value: '#f3f4f6' },
  { name: 'Teal Primary', value: '#00b48f' },
  { name: 'Dark Green', value: '#00461a' },
  { name: 'Light Teal', value: '#e6f7f4' },
  { name: 'Light Green', value: '#f0f7f4' },
];

const ImpactPageBuilder = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [impactStats, setImpactStats] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    sections: []
  });

  useEffect(() => {
    fetchCurrentPage();
    fetchImpactStats();
  }, []);

  const fetchCurrentPage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/page/current`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });
      const data = await response.json();
      
      if (data) {
        setPage(data);
        setFormData({
          title: data.title,
          sections: data.sections
        });
      }
    } catch (error) {
      console.error('Error fetching page:', error);
    }
  };

  const fetchImpactStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setImpactStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Section Management (same as newsletter)
  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      columns: [{ id: `col-${Date.now()}`, blocks: [] }],
      columnLayout: 1,
      backgroundColor: '#ffffff',
      padding: '20px'
    };
    setFormData({
      ...formData,
      sections: [...formData.sections, newSection]
    });
  };

  const deleteSection = (sectionIndex) => {
    const updatedSections = formData.sections.filter((_, i) => i !== sectionIndex);
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateSectionLayout = (sectionIndex, layout) => {
    const updatedSections = [...formData.sections];
    const currentColumns = updatedSections[sectionIndex].columns.length;
    
    if (layout > currentColumns) {
      for (let i = currentColumns; i < layout; i++) {
        updatedSections[sectionIndex].columns.push({
          id: `col-${Date.now()}-${i}`,
          blocks: []
        });
      }
    } else if (layout < currentColumns) {
      updatedSections[sectionIndex].columns = updatedSections[sectionIndex].columns.slice(0, layout);
    }
    
    updatedSections[sectionIndex].columnLayout = layout;
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateSectionColor = (sectionIndex, color) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].backgroundColor = color;
    setFormData({ ...formData, sections: updatedSections });
  };

  // Block Management
  const addBlock = (sectionIndex, columnIndex, blockType) => {
    const updatedSections = [...formData.sections];
    let newBlock;
    
    const blockId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    switch (blockType) {
      case 'image':
        newBlock = { id: blockId, type: 'image', url: '', alt: '', link: '', width: '100%' };
        break;
      case 'imageWithText':
        newBlock = { 
          id: blockId, 
          type: 'imageWithText', 
          url: '', 
          alt: '', 
          link: '',
          imagePosition: 'left',
          content: '<p>Your text content here...</p>'
        };
        break;
      case 'heading':
        newBlock = { id: blockId, type: 'heading', level: 'h2', text: 'Heading Text', color: '#000000' };
        break;
      case 'paragraph':
        newBlock = { id: blockId, type: 'paragraph', content: '<p>Enter your text here...</p>' };
        break;
      case 'button':
        newBlock = { id: blockId, type: 'button', text: 'Learn More', link: '', backgroundColor: '#00b48f', textColor: '#ffffff', alignment: 'center' };
        break;
      case 'accordion':
        newBlock = { 
          id: blockId, 
          type: 'accordion', 
          title: 'Accordion Title', 
          content: '<p>Accordion content goes here...</p>', 
          titleColor: '#ffffff',
          titleBgColor: '#00b48f',
          contentColor: '#374151',
          contentBgColor: '#f3f4f6'
        };
        break;
      case 'impact_stats':
        newBlock = { id: blockId, type: 'impact_stats', layout: 'grid' };
        break;
      default:
        return;
    }
    
    updatedSections[sectionIndex].columns[columnIndex].blocks.push(newBlock);
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateBlock = (sectionIndex, columnIndex, blockIndex, updates) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].columns[columnIndex].blocks[blockIndex] = {
      ...updatedSections[sectionIndex].columns[columnIndex].blocks[blockIndex],
      ...updates
    };
    setFormData({ ...formData, sections: updatedSections });
  };

  const deleteBlock = (sectionIndex, columnIndex, blockIndex) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].columns[columnIndex].blocks = 
      updatedSections[sectionIndex].columns[columnIndex].blocks.filter((_, i) => i !== blockIndex);
    setFormData({ ...formData, sections: updatedSections });
  };

  const handleImageUpload = async (sectionIndex, columnIndex, blockIndex, file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (response.ok) {
        const data = await response.json();
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        updateBlock(sectionIndex, columnIndex, blockIndex, { url: imageUrl });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    }
  };

  const savePage = async () => {
    if (!formData.title) {
      alert('Please enter a page title');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (page) {
        // Update existing page
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/page/${page.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          alert('Impact page saved successfully!');
          fetchCurrentPage();
        } else {
          const error = await response.json();
          alert(`Error: ${error.detail}`);
        }
      } else {
        // Create new page
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/page/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          alert('Impact page created successfully!');
          fetchCurrentPage();
        } else {
          const error = await response.json();
          alert(`Error: ${error.detail}`);
        }
      }
    } catch (error) {
      console.error('Error saving page:', error);
      alert('Failed to save page');
    }
    setLoading(false);
  };

  const publishPage = async () => {
    if (!formData.title) {
      alert('Please enter a page title');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // First save if no page exists
      if (!page) {
        const createResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/page/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (!createResponse.ok) {
          const error = await createResponse.json();
          alert(`Error: ${error.detail}`);
          setLoading(false);
          return;
        }
        
        const createData = await createResponse.json();
        
        // Publish
        const publishResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/page/${createData.page_id}/publish`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (publishResponse.ok) {
          alert('Impact page published successfully!');
          fetchCurrentPage();
        }
      } else {
        // Update then publish
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/page/${page.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        const publishResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/page/${page.id}/publish`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (publishResponse.ok) {
          alert('Impact page published successfully!');
          fetchCurrentPage();
        } else {
          const error = await publishResponse.json();
          alert(`Error: ${error.detail}`);
        }
      }
    } catch (error) {
      console.error('Error publishing page:', error);
      alert('Failed to publish page');
    }
    setLoading(false);
  };

  // Impact Stats Widget Renderer
  const renderImpactStats = (layout) => {
    if (!impactStats) return <div className="text-gray-500">Loading stats...</div>;
    
    const stats = [
      { label: 'Boxes Reused', value: impactStats.boxes_reused, color: 'emerald' },
      { label: 'Trees Saved', value: impactStats.trees_saved, color: 'green' },
      { label: 'Litres Water Saved', value: impactStats.water_saved, color: 'blue' },
      { label: 'Tonnes CO₂ Saved', value: impactStats.co2_saved, color: 'purple' },
      { label: 'KwH Energy Saved', value: impactStats.energy_saved, color: 'orange' }
    ];

    if (layout === 'grid') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {stats.map((stat, i) => (
            <div key={i} className={`bg-${stat.color}-50 rounded-xl p-6 text-center`}>
              <div className={`text-4xl font-bold text-${stat.color}-900 mb-2`}>{stat.value}</div>
              <div className={`text-sm text-${stat.color}-700`}>{stat.label}</div>
            </div>
          ))}
        </div>
      );
    } else if (layout === 'cards') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat, i) => (
            <div key={i} className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-emerald-500">
              <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <div className="flex flex-wrap gap-8 justify-center">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-5xl font-bold text-emerald-600">{stat.value}</div>
              <div className="text-gray-600 mt-2">{stat.label}</div>
            </div>
          ))}
        </div>
      );
    }
  };

  // Preview Component
  const ImpactPagePreview = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h3 className="text-xl font-bold">Preview: {formData.title}</h3>
          <button
            onClick={() => setShowPreview(false)}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>
        <div className="p-6">
          {formData.sections.map((section, sIndex) => (
            <div
              key={section.id}
              style={{
                backgroundColor: section.backgroundColor,
                padding: section.padding
              }}
              className="mb-4 rounded-lg"
            >
              <div className={`grid gap-4 ${
                section.columnLayout === 1 ? 'grid-cols-1' :
                section.columnLayout === 2 ? 'grid-cols-2' :
                section.columnLayout === 3 ? 'grid-cols-3' :
                'grid-cols-4'
              }`}>
                {section.columns.map((column, cIndex) => (
                  <div key={column.id}>
                    {column.blocks.map((block, bIndex) => {
                      if (block.type === 'image') {
                        return (
                          <div key={bIndex} className="mb-4">
                            {block.link ? (
                              <a href={block.link} target="_blank" rel="noopener noreferrer">
                                <img src={block.url} alt={block.alt} style={{ width: block.width }} className="rounded" />
                              </a>
                            ) : (
                              <img src={block.url} alt={block.alt} style={{ width: block.width }} className="rounded" />
                            )}
                          </div>
                        );
                      } else if (block.type === 'heading') {
                        const HeadingTag = block.level;
                        return (
                          <HeadingTag 
                            key={bIndex} 
                            style={{ color: block.color }} 
                            className={`mb-4 font-bold ${
                              block.level === 'h1' ? 'text-4xl' :
                              block.level === 'h2' ? 'text-3xl' :
                              'text-2xl'
                            }`}
                          >
                            {block.text}
                          </HeadingTag>
                        );
                      } else if (block.type === 'paragraph') {
                        return <div key={bIndex} className="mb-4 prose max-w-none" dangerouslySetInnerHTML={{ __html: block.content }} />;
                      } else if (block.type === 'button') {
                        return (
                          <div key={bIndex} className={`mb-4 text-${block.alignment}`}>
                            <a
                              href={block.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                backgroundColor: block.backgroundColor,
                                color: block.textColor
                              }}
                              className="inline-block px-6 py-3 rounded-lg font-semibold hover:opacity-90"
                            >
                              {block.text}
                            </a>
                          </div>
                        );
                      } else if (block.type === 'impact_stats') {
                        return (
                          <div key={bIndex} className="mb-4">
                            {renderImpactStats(block.layout)}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 overflow-x-hidden">
      {showPreview && <ImpactPagePreview />}
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Impact Page Builder</h1>
              <p className="text-sm text-gray-500">Create and manage environmental impact content</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {page && (
              <span className={`px-4 py-2 rounded-lg text-sm font-medium text-center ${
                page.status === 'published' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-200 text-gray-700'
              }`}>
                {page.status === 'published' ? 'Published' : 'Draft'}
              </span>
            )}
            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        {/* Title */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
            placeholder="e.g., Our Environmental Impact"
          />
        </div>

        {/* Sections */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Page Sections</h3>
            <button
              onClick={addSection}
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Section
            </button>
          </div>

          <div className="space-y-6">
            {formData.sections.map((section, sIndex) => (
              <div key={section.id} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start mb-4">
                  <span className="text-sm font-medium text-gray-500">Section {sIndex + 1}</span>
                  <button
                    onClick={() => deleteSection(sIndex)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Section Settings */}
                <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-white rounded">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Column Layout</label>
                    <select
                      value={section.columnLayout}
                      onChange={(e) => updateSectionLayout(sIndex, parseInt(e.target.value))}
                      className="w-full px-3 py-1 text-sm border rounded"
                    >
                      <option value={1}>1 Column</option>
                      <option value={2}>2 Columns</option>
                      <option value={3}>3 Columns</option>
                      <option value={4}>4 Columns</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Background Color</label>
                    <select
                      value={section.backgroundColor}
                      onChange={(e) => updateSectionColor(sIndex, e.target.value)}
                      className="w-full px-3 py-1 text-sm border rounded"
                    >
                      {BRAND_COLORS.map(color => (
                        <option key={color.value} value={color.value}>
                          {color.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Columns */}
                <div className={`grid gap-4 ${
                  section.columnLayout === 1 ? 'grid-cols-1' :
                  section.columnLayout === 2 ? 'grid-cols-2' :
                  section.columnLayout === 3 ? 'grid-cols-3' :
                  'grid-cols-4'
                }`}>
                  {section.columns.map((column, cIndex) => (
                    <div key={column.id} className="border border-dashed border-gray-300 rounded p-3 min-h-[100px] bg-white">
                      <div className="text-xs font-medium text-gray-500 mb-2">Column {cIndex + 1}</div>
                      
                      {/* Add Block Buttons */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'image')}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          title="Add Image"
                        >
                          <ImageIcon className="w-3 h-3 inline mr-1" />Img
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'imageWithText')}
                          className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                          title="Add Image with Text"
                        >
                          <FileText className="w-3 h-3 inline mr-1" />Img+T
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'heading')}
                          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                          title="Add Heading"
                        >
                          <Type className="w-3 h-3 inline mr-1" />H
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'paragraph')}
                          className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                          title="Add Rich Text"
                        >
                          <AlignLeft className="w-3 h-3 inline mr-1" />Text
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'button')}
                          className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                          title="Add Button"
                        >
                          <Square className="w-3 h-3 inline mr-1" />Btn
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'accordion')}
                          className="px-2 py-1 text-xs bg-teal-100 text-teal-700 rounded hover:bg-teal-200"
                          title="Add Accordion"
                        >
                          <List className="w-3 h-3 inline mr-1" />Acc
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'impact_stats')}
                          className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                          title="Add Impact Stats Widget"
                        >
                          <TrendingUp className="w-3 h-3 inline mr-1" />Stats
                        </button>
                      </div>

                      {/* Blocks */}
                      <div className="space-y-3">
                        {column.blocks.map((block, bIndex) => (
                          <div key={bIndex} className="border border-gray-200 rounded p-2 bg-gray-50">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs font-medium text-gray-600 capitalize">{block.type.replace('_', ' ')}</span>
                              <button
                                onClick={() => deleteBlock(sIndex, cIndex, bIndex)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>

                            {/* Block-specific inputs (same as newsletter but add impact_stats) */}
                            {block.type === 'image' && (
                              <div className="space-y-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(sIndex, cIndex, bIndex, e.target.files[0])}
                                  className="w-full text-xs"
                                />
                                {block.url && <img src={block.url} alt="Preview" className="w-full h-20 object-cover rounded" />}
                                <input
                                  type="text"
                                  placeholder="Alt text"
                                  value={block.alt}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { alt: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                />
                                <input
                                  type="text"
                                  placeholder="Link URL (optional)"
                                  value={block.link}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { link: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                />
                              </div>
                            )}

                            {block.type === 'heading' && (
                              <div className="space-y-2">
                                <select
                                  value={block.level}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { level: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                >
                                  <option value="h1">H1</option>
                                  <option value="h2">H2</option>
                                  <option value="h3">H3</option>
                                </select>
                                <input
                                  type="text"
                                  value={block.text}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { text: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                  placeholder="Heading text"
                                />
                              </div>
                            )}

                            {block.type === 'paragraph' && (
                              <div className="space-y-2">
                                <RichTextEditor
                                  value={block.content}
                                  onChange={(value) => updateBlock(sIndex, cIndex, bIndex, { content: value })}
                                  placeholder="Enter your content..."
                                />
                              </div>
                            )}

                            {block.type === 'imageWithText' && (
                              <div className="space-y-2">
                                <select
                                  value={block.imagePosition || 'left'}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { imagePosition: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                >
                                  <option value="left">Image on Left</option>
                                  <option value="right">Image on Right</option>
                                </select>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(sIndex, cIndex, bIndex, e.target.files[0])}
                                  className="w-full text-xs"
                                />
                                {block.url && <img src={block.url} alt="Preview" className="w-full h-20 object-cover rounded" />}
                                <input
                                  type="text"
                                  placeholder="Alt text"
                                  value={block.alt || ''}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { alt: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                />
                                <input
                                  type="text"
                                  placeholder="Link URL (optional)"
                                  value={block.link || ''}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { link: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                />
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Text Content</label>
                                  <RichTextEditor
                                    value={block.content || ''}
                                    onChange={(value) => updateBlock(sIndex, cIndex, bIndex, { content: value })}
                                    placeholder="Enter text content..."
                                  />
                                </div>
                              </div>
                            )}

                            {block.type === 'button' && (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  placeholder="Button text"
                                  value={block.text}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { text: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                />
                                <input
                                  type="text"
                                  placeholder="Link URL"
                                  value={block.link}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { link: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                />
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-xs text-gray-600">Background</label>
                                    <input
                                      type="color"
                                      value={block.backgroundColor || '#00b48f'}
                                      onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { backgroundColor: e.target.value })}
                                      className="w-full h-6"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-600">Text Color</label>
                                    <input
                                      type="color"
                                      value={block.textColor || '#ffffff'}
                                      onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { textColor: e.target.value })}
                                      className="w-full h-6"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-1 flex-wrap items-center">
                                  <span className="text-xs text-gray-500">Brand colors:</span>
                                  {BRAND_COLORS.map((color) => (
                                    <button
                                      key={color.value}
                                      onClick={() => updateBlock(sIndex, cIndex, bIndex, { backgroundColor: color.value })}
                                      className="w-6 h-6 rounded border-2 border-gray-300 hover:border-teal-500"
                                      style={{ backgroundColor: color.value }}
                                      title={`Set to ${color.name}`}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                            {block.type === 'accordion' && (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  placeholder="Accordion title"
                                  value={block.title || ''}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { title: e.target.value })}
                                  className="w-full px-2 py-1 text-sm font-medium border rounded"
                                />
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Content</label>
                                  <RichTextEditor
                                    value={block.content || ''}
                                    onChange={(value) => updateBlock(sIndex, cIndex, bIndex, { content: value })}
                                    placeholder="Enter accordion content..."
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-xs text-gray-600">Title Text</label>
                                    <input
                                      type="color"
                                      value={block.titleColor || '#ffffff'}
                                      onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { titleColor: e.target.value })}
                                      className="w-full h-6"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-600">Title BG</label>
                                    <input
                                      type="color"
                                      value={block.titleBgColor || '#00b48f'}
                                      onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { titleBgColor: e.target.value })}
                                      className="w-full h-6"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-600">Content Text</label>
                                    <input
                                      type="color"
                                      value={block.contentColor || '#374151'}
                                      onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { contentColor: e.target.value })}
                                      className="w-full h-6"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-600">Content BG</label>
                                    <input
                                      type="color"
                                      value={block.contentBgColor || '#f3f4f6'}
                                      onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { contentBgColor: e.target.value })}
                                      className="w-full h-6"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-1 flex-wrap items-center">
                                  <span className="text-xs text-gray-500">Quick title BG:</span>
                                  {BRAND_COLORS.map((color) => (
                                    <button
                                      key={color.value}
                                      onClick={() => updateBlock(sIndex, cIndex, bIndex, { titleBgColor: color.value })}
                                      className="w-6 h-6 rounded border-2 border-gray-300 hover:border-teal-500"
                                      style={{ backgroundColor: color.value }}
                                      title={`Set to ${color.name}`}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                            {block.type === 'impact_stats' && (
                              <div className="space-y-2">
                                <label className="text-xs text-gray-600">Display Style</label>
                                <select
                                  value={block.layout}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { layout: e.target.value })}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                >
                                  <option value="grid">Grid</option>
                                  <option value="cards">Cards</option>
                                  <option value="minimal">Minimal</option>
                                </select>
                                <div className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                                  ℹ️ This widget will automatically display the latest impact statistics
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            onClick={savePage}
            disabled={loading}
            className="flex items-center px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </button>
          <button
            onClick={publishPage}
            disabled={loading}
            className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Send className="w-4 h-4 mr-2" />
            Publish
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpactPageBuilder;
